import {
  getCreatorChannelAudience,
  getCreatorAudienceUploadTarget,
  hasCreatorIncludedAudienceData,
  hasCreatorChannelAudience,
  isCreatorAudienceAnalysisUnsupported
} from '@/biz/multiPlatformCollaboration/audience';
import { PlatformIconComponent } from '@/biz/platform';
import CreatorAudienceCollapseChevron from '@/components/multiPlatformCollaboration/CreatorAudienceCollapseChevron';
import CreatorAudienceSourceBadge from './CreatorAudienceSourceBadge';
import CreatorPackageAudienceNotice from '@/components/multiPlatformCollaboration/CreatorPackageAudienceNotice';
import type { CreatorPackageAudiencePreviewProps } from '@/components/multiPlatformCollaboration/CreatorPackageAudiencePreview';
import AudienceDataActionButtons from '@/components/web/components/AudienceDataActionButtons';
import { AudienceCharts } from '@/components/web/components/ModalTakeOrder/audienceDemographics';
import { useEffect, useRef, useState } from 'react';
import { Loading } from 'aha-components';
import { TriangleExclamationIco2 } from '@/components/common/icon';
import toast from 'react-hot-toast';
import { usePlatformStore } from '@/hooks/usePlatformStore';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { showSocialBindingBrowserFallbackIfNeeded } from '@/app/socialBinding/browserFallback';
import { openPhylloPlatformConnect } from '@/biz/openPhylloPlatformConnect';
import ModalUpdataAudience from '@/components/web/components/ModalUpdataAudience';
import type { CreatorPlacement } from '@/biz/multiPlatformCollaboration/model';
import { useTranslation } from 'react-i18next';

/** Reuse the existing charts with each channel's own data, never preview numbers. */
export default function CreatorPackageAudienceData({
  collaborationPackage,
  includedPlacementIds,
  audienceViews,
  onRefreshAudience,
  failedAudienceKeys,
  onRetryAudienceAnalysis,
  onValidityChange,
  audienceError = false
}: CreatorPackageAudiencePreviewProps) {
  const { t } = useTranslation();
  const [uploadPlacement, setUploadPlacement] =
    useState<CreatorPlacement | null>(null);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const linkInFlight = useRef(false);
  const awaitingConnection = useRef(false);
  const [waitingTime, setWaitingTime] = useState(0);
  const included = new Set(includedPlacementIds);
  const getAudience = (placement: CreatorPlacement) =>
    getCreatorChannelAudience(placement, collaborationPackage, audienceViews);
  const hasAudience = (placement: CreatorPlacement) =>
    hasCreatorChannelAudience(placement, collaborationPackage, audienceViews);
  const isAudienceLoading = (placement: CreatorPlacement) => {
    const audience = getAudience(placement);
    return (
      audience?.source === 'self_estimated' &&
      (audience.status === 'processing' || audience.status === 'pending')
    );
  };
  const placements = collaborationPackage.placements.filter(
    (placement) => included.has(placement.id) && placement.raw?.active !== false
  );
  const processing = placements.some(isAudienceLoading);
  useEffect(() => {
    if (!processing) {
      setWaitingTime(0);
      return;
    }
    const waitingTimer = window.setInterval(
      () => setWaitingTime((time) => time + 1),
      1000
    );
    return () => {
      window.clearInterval(waitingTimer);
    };
  }, [processing]);
  useEffect(() => {
    const onFocus = () => {
      if (!awaitingConnection.current) return;
      awaitingConnection.current = false;
      if (onRefreshAudience) {
        void onRefreshAudience().catch((error: Error) =>
          toast.error(error.message)
        );
      } else {
        void useDetailDataStore
          .getState()
          .reQueryDetailData({ orderId: collaborationPackage.id })
          .catch((error: Error) => toast.error(error.message));
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [collaborationPackage.id, onRefreshAudience]);
  const linkChannel = async (placement: CreatorPlacement) => {
    if (linkInFlight.current) return;
    linkInFlight.current = true;
    setLinkingId(placement.id);
    try {
      if (await showSocialBindingBrowserFallbackIfNeeded()) return;
      await usePlatformStore.getState().fetchPlatformData();
      const { platformList, phylloSDKConfig } = usePlatformStore.getState();
      const platform = platformList.find(
        (item: any) => item.platform === placement.platform
      );
      if (!platform?.id)
        throw new Error(
          t('multi_platform.quote.connection_unavailable', {
            defaultValue:
              'This platform does not support channel connection. Please upload screenshots.'
          })
        );
      awaitingConnection.current = true;
      const opened = await openPhylloPlatformConnect({
        workPlatformId: platform.id,
        platformType: placement.platform,
        phylloSDKConfig,
        onboarding: false,
        validateOrderId: false,
        urlShowModalAnalysis: false
      });
      if (!opened) awaitingConnection.current = false;
      // OAuth success is not audience completion; the refreshed channel data is authoritative.
    } catch (error) {
      awaitingConnection.current = false;
      toast.error(
        error instanceof Error ? error.message : 'Channel connection failed.'
      );
    } finally {
      linkInFlight.current = false;
      setLinkingId(null);
    }
  };
  const uploadTarget = uploadPlacement
    ? getCreatorAudienceUploadTarget(collaborationPackage, uploadPlacement)
    : null;
  const valid = hasCreatorIncludedAudienceData(
    collaborationPackage,
    includedPlacementIds,
    audienceViews
  );
  useEffect(() => {
    onValidityChange?.(valid);
  }, [valid, onValidityChange]);
  const renderActionButtons = (
    placement: CreatorPlacement,
    stacked: boolean
  ) => (
    <AudienceDataActionButtons
      stacked={stacked}
      responsive={!stacked}
      linkLoading={linkingId === placement.id}
      onUpload={() => {
        if (!getCreatorAudienceUploadTarget(collaborationPackage, placement)) {
          toast.error(
            t('multi_platform.quote.audience_target_missing', {
              defaultValue:
                'Cannot identify this channel for audience screenshot upload. Please refresh the order and try again.'
            })
          );
          return;
        }
        setUploadPlacement(placement);
      }}
      onLink={() => {
        void linkChannel(placement);
      }}
    />
  );
  return (
    <section className="mt-7 w-full">
      <h3 className="m-0 text-base font-semibold leading-6 text-[#292524]">
        {t('multi_platform.quote.audience_title')}
      </h3>
      <CreatorPackageAudienceNotice />
      <div className="mt-3 flex flex-col gap-3">
        {placements.map((placement) => {
          const hasError = audienceError && !hasAudience(placement);
          return (
            <div key={placement.channelKey || placement.id}>
              <details
                open
                data-audience-channel-key={placement.channelKey}
                data-audience-invalid={hasError ? 'true' : undefined}
                className={`group/audience rounded-lg border bg-white ${hasError ? 'border-red-500' : 'border-[#E7E5E4]'}`}
              >
                <summary
                  data-testid={`audience-header-${placement.id}`}
                  className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm text-[#525252] [&::-webkit-details-marker]:hidden"
                >
                  <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    <PlatformIconComponent
                      platform={placement.platform}
                      className="h-[18px] w-[18px] shrink-0"
                    />
                    <span className="min-w-0 truncate">
                      @
                      {String(
                        placement.userName || placement.raw?.displayName || '-'
                      ).replace(/^@+/, '')}
                    </span>
                    <span className="font-semibold text-[#0D0D0D]">
                      {t(
                        'modal.modal_take_order.audience_demographics.details',
                        {
                          defaultValue: 'Details'
                        }
                      )}
                    </span>
                    {hasAudience(placement) ? (
                      <span className="border-l border-[#E5E5E5] pl-2">
                        <CreatorAudienceSourceBadge
                          source={getAudience(placement)?.source}
                          status={getAudience(placement)?.status}
                        />
                      </span>
                    ) : null}
                  </span>
                  <CreatorAudienceCollapseChevron className="rotate-180 group-open/audience:rotate-0" />
                </summary>
                {hasAudience(placement) ? (
                  <AudienceCharts
                    audienceData={getAudience(placement)}
                    variant="channel"
                  />
                ) : isAudienceLoading(placement) ? (
                  <div
                    data-testid={`audience-loading-${placement.id}`}
                    className="relative min-h-[308px] overflow-hidden"
                  >
                    <img
                      src="/upload-screenshots-bg.png"
                      alt=""
                      aria-hidden
                      className="absolute inset-0 h-full w-full object-cover object-center"
                    />
                    <div className="relative z-10 flex min-h-[308px] flex-col items-center justify-center gap-2 px-4 py-5 text-center">
                      <Loading width={48} height={48} color="#050505" />
                      <p className="m-0 max-w-[380px] text-sm font-medium text-[#292524]">
                        {t(
                          'modal.modal_take_order.audience_demographics.reading_likes_comments',
                          {
                            defaultValue:
                              "Reading your channel's likes & comments to map your audience's location, gender and age."
                          }
                        )}
                      </p>
                      <span className="text-xs text-[#737373]">
                        {waitingTime}s
                      </span>
                      <p className="m-0 text-xs text-[#737373]">
                        {t(
                          'modal.modal_take_order.audience_demographics.dont_want_to_wait',
                          {
                            defaultValue:
                              "Don't want to wait? Share it directly"
                          }
                        )}
                      </p>
                      <div className="mt-2 w-full">
                        {renderActionButtons(placement, true)}
                      </div>
                    </div>
                  </div>
                ) : getAudience(placement)?.source === 'none' &&
                  getAudience(placement)?.reason === 'insufficient_audience' ? (
                  <div
                    className="flex min-h-[160px] flex-col items-center justify-center gap-2 px-4 py-5 text-center"
                    style={{
                      backgroundImage: "url('/upload-screenshots-bg.png')"
                    }}
                  >
                    <TriangleExclamationIco2 className="h-5 w-5" />
                    <p className="m-0 text-sm font-medium text-[#292524]">
                      {t(
                        'modal.modal_take_order.audience_demographics.not_enough_public_engagement',
                        {
                          defaultValue:
                            'Not enough public engagement to analyze yet'
                        }
                      )}
                    </p>
                    <p className="m-0 max-w-[350px] text-xs text-[#737373]">
                      {t(
                        'modal.modal_take_order.audience_demographics.not_enough_public_engagement_tip',
                        {
                          defaultValue:
                            "This account doesn't have enough likes and comments yet for a reliable audience profile. Upload your analytics or link your channel so we can analyze your audience directly."
                        }
                      )}
                    </p>
                  </div>
                ) : (
                  <div
                    className="flex min-h-[120px] flex-col items-center justify-center px-4 py-5 text-center"
                    style={{
                      backgroundImage: "url('/upload-screenshots-bg.png')"
                    }}
                  >
                    <p className="m-0 text-sm font-semibold text-[#292524]">
                      {t(
                        'modal.modal_take_order.audience_demographics.select_method',
                        {
                          defaultValue: 'Select method'
                        }
                      )}
                    </p>
                    <p className="mt-2 text-xs text-[#737373]">
                      {isCreatorAudienceAnalysisUnsupported(
                        getAudience(placement)
                      )
                        ? t(
                            'multi_platform.quote.channel_audience_analysis_unsupported',
                            {
                              defaultValue:
                                'Automatic audience analysis is not supported for this channel. Upload audience screenshots, or link your channel if connection is available.'
                            }
                          )
                        : t('multi_platform.quote.channel_audience_missing', {
                            defaultValue:
                              'Audience data is not available for this channel. Link your channel or upload audience screenshots.'
                          })}
                    </p>
                    {placement.channelKey &&
                    !isCreatorAudienceAnalysisUnsupported(
                      getAudience(placement)
                    ) &&
                    failedAudienceKeys?.has(placement.channelKey) &&
                    onRetryAudienceAnalysis ? (
                      <button
                        type="button"
                        className="mt-2 text-sm font-semibold text-[#FF5A1F]"
                        onClick={() =>
                          onRetryAudienceAnalysis(placement.channelKey!)
                        }
                      >
                        {t('multi_platform.quote.retry_audience_analysis', {
                          defaultValue: 'Retry analysis'
                        })}
                      </button>
                    ) : null}
                  </div>
                )}
                {!isAudienceLoading(placement) ? (
                  <div className="px-4 pb-4 pt-3">
                    {hasAudience(placement) &&
                    getAudience(placement)?.source === 'self_estimated' ? (
                      <p className="mb-3 border-t border-[#E5E5E5] pt-3 text-xs leading-[18px] text-[#737373]">
                        {t(
                          'modal.modal_take_order.audience_demographics.estimates_tip',
                          {
                            defaultValue:
                              "These are estimates. To show your exact numbers, link your channel or upload screenshots. It's optional."
                          }
                        )}
                      </p>
                    ) : null}
                    {renderActionButtons(placement, !hasAudience(placement))}
                  </div>
                ) : null}
              </details>
              {hasError ? (
                <p
                  className="mb-0 mt-2 text-sm font-medium text-red-500"
                  role="alert"
                >
                  {t(
                    'modal.modal_take_order.audience_demographics.audience_error',
                    {
                      defaultValue:
                        'Complete your audience demographics to submit your quote'
                    }
                  )}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
      {uploadTarget ? (
        <ModalUpdataAudience
          showModalUpdataAudience={true}
          setShowModalUpdataAudience={(open) => {
            if (!open) setUploadPlacement(null);
          }}
          platform={uploadTarget.platform}
          audienceTarget={uploadTarget}
          uploadCallback={() => {
            if (onRefreshAudience) {
              void onRefreshAudience().catch((error: Error) =>
                toast.error(error.message)
              );
            } else {
              void useDetailDataStore
                .getState()
                .reQueryDetailData({ orderId: collaborationPackage.id })
                .catch((error: Error) => toast.error(error.message));
            }
          }}
        />
      ) : null}
    </section>
  );
}
